// The capability table and its derivations (sdk ADR-012).
import type {
  AcquisitionSource,
  CredentialIdentity,
  CredentialMaterial,
  DegradedReason,
  HolderPhase,
  HolderPhaseName,
} from '../auth/authState.js';

export interface PhaseCapabilities {
  holdsMaterial: boolean;
  holdsIdentity: boolean;
  servesBearer: boolean;
  blocksUI: boolean;
  renewable: boolean;
}

export type CapabilityColumn = keyof PhaseCapabilities;

export const PHASE_CAPABILITIES: { readonly [K in HolderPhaseName]: PhaseCapabilities } = {
  no_credential: {
    holdsMaterial: false,
    holdsIdentity: false,
    servesBearer: false,
    blocksUI: true,
    renewable: false,
  },
  acquiring: {
    holdsMaterial: false,
    holdsIdentity: false,
    servesBearer: false,
    blocksUI: true,
    renewable: false,
  },
  unactivated: {
    holdsMaterial: true,
    holdsIdentity: false,
    servesBearer: true,
    blocksUI: false,
    renewable: false,
  },
  usable: {
    holdsMaterial: true,
    holdsIdentity: true,
    servesBearer: true,
    blocksUI: false,
    renewable: true,
  },
  degraded: {
    holdsMaterial: true,
    holdsIdentity: true,
    servesBearer: true,
    blocksUI: false,
    renewable: true,
  },
};

export const ALL_HOLDER_PHASES = Object.keys(PHASE_CAPABILITIES) as HolderPhaseName[];

export const CAPABILITY_COLUMNS = Object.keys(
  PHASE_CAPABILITIES.no_credential,
) as CapabilityColumn[];

export type PhaseOrName = HolderPhase | HolderPhaseName;

function nameOf(phase: PhaseOrName): HolderPhaseName {
  return typeof phase === 'string' ? phase : phase.phase;
}

export function capabilitiesOf(phase: PhaseOrName): PhaseCapabilities {
  return PHASE_CAPABILITIES[nameOf(phase)];
}

export function holdsMaterial(phase: PhaseOrName): boolean {
  return capabilitiesOf(phase).holdsMaterial;
}

export function holdsIdentity(phase: PhaseOrName): boolean {
  return capabilitiesOf(phase).holdsIdentity;
}

export function servesBearer(phase: PhaseOrName): boolean {
  return capabilitiesOf(phase).servesBearer;
}

export function blocksUI(phase: PhaseOrName): boolean {
  return capabilitiesOf(phase).blocksUI;
}

export function isRenewable(phase: PhaseOrName): boolean {
  return capabilitiesOf(phase).renewable;
}

export function materialOf(phase: HolderPhase): CredentialMaterial | null {
  return 'material' in phase ? phase.material : null;
}

export function identityOf(phase: HolderPhase): CredentialIdentity | null {
  return 'identity' in phase ? phase.identity : null;
}

export function expiresAtOf(phase: HolderPhase): number | null {
  return 'expires_at' in phase ? phase.expires_at : null;
}

export function degradedReasonOf(phase: HolderPhase): DegradedReason | null {
  return phase.phase === 'degraded' ? phase.reason : null;
}

export function acquisitionSourceOf(phase: HolderPhase): AcquisitionSource | null {
  return 'source' in phase ? phase.source : null;
}

export function bearerOf(phase: HolderPhase): string | null {
  if (!servesBearer(phase)) return null;
  const material = materialOf(phase);
  if (material === null) {
    throw new Error(
      `sdk.lifecycle.capabilities: ${phase.phase} serves a bearer but holds no material — ` +
        'the capability table and the phase union disagree',
    );
  }
  return material.session_jwt;
}
